/**
 * A filter option's wire value is decided by its SHAPE, not by its `filter_id`: the backend serves
 * entity filters as `{ id, name }` (filter by the id) and value filters as plain strings (filter by
 * the string itself) — see the platform contract stated in orders'
 * `evaluations-filters-values.service.ts`.
 *
 * The same `filter_id` can be served both ways, so no `filter_id` allow-list can be right for both:
 * `categories` is `{ id, name }` on `/evaluations/filters/categories` and a plain name on the
 * catalogue's. Listing it sent `categories=null` to the catalogue; omitting it sent
 * `categories=[object Object]` to évaluations.
 */
export function filterValueKey(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value === "object") {
    const id = (value as { id?: unknown }).id;
    return id === null || id === undefined ? "null" : String(id);
  }
  return String(value);
}

/** True when a filter's options are entity objects (`{ id, name }`) rather than plain values. */
export function optionsAreEntities(values?: unknown[]): boolean {
  return !!values?.some((v) => v !== null && typeof v === "object");
}

/**
 * What every `…/filters` route answers, across the four SDK section models that describe it
 * (`FilterSectionResponse`, `EvaluationFilterSection`, `UserDetail{Request,Training}FilterSectionModel`).
 * They are structurally identical; only `filterName` is narrower on some of them.
 */
export interface IFilterSectionResponse {
  sectionId: string;
  filters: IFilterItemResponse[];
}

export interface IFilterItemResponse {
  filterId: string;
  filterName?: string | null;
  /** `default` is a reserved word, so the SDK exposes the wire's `default` under this name. */
  _default?: boolean | null;
  available?: boolean | null;
}

/**
 * One filter, as the bar holds it. The wire is snake_case and the SDK models map it to camelCase on
 * the way in (`filter_id` → `filterId`, `default` → `_default`); this is where the bar's own
 * snake_case shape is rebuilt, so `_default` is the one field that must never be read as `default`.
 */
export function normalizeFilterOption(filter: IFilterItemResponse) {
  return {
    filterId: filter.filterId,
    filterName: filter.filterName ?? undefined,
    default: filter._default ?? false,
    available: filter.available ?? false,
    active: false,
    selectedValues: [] as any[],
  };
}
